import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { LoadingListItems } from '../../../interfaces/LoadingListItems.interfaces';
import { LoadingLists } from '../../../interfaces/LoadingLists.interfaces';
import { LoadingListItemsService } from '../../../services/loading-list-items.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-loading-list-items',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './loading-list-items.component.html',
  styleUrls: ['./loading-list-items.component.css']
})
export class LoadingListItemsComponent implements OnChanges {

  @Input() visible = false;
  @Input() loadingList: LoadingLists | null = null;

  @Output() closed = new EventEmitter<void>();

  isLoading = false;

  items: LoadingListItems[] = [];
  filteredItems: LoadingListItems[] = [];
  pagedItems: LoadingListItems[] = [];

  searchTerm = '';
  filterStatus = '';
  availableStatuses: string[] = [];

  selectedIds = new Set<number>();

  itemsPerPage = 10;
  currentPage = 1;

  isExporting = false;
  isPrinting = false;

  constructor(private itemsService: LoadingListItemsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const openedNow = changes['visible'] && this.visible;
    const changedListWhileOpen = changes['loadingList'] && this.visible;

    if (openedNow || changedListWhileOpen) {
      const id = this.loadingList?.loadingListId;
      if (id) this.loadItems(id);
    }

    if (changes['visible'] && !this.visible) {
      this.resetLocalState();
    }
  }

  close(): void {
    this.visible = false;
    this.closed.emit();
  }

  private loadItems(loadingListId: number): void {
    this.isLoading = true;
    this.items = [];
    this.filteredItems = [];
    this.pagedItems = [];
    this.availableStatuses = [];
    this.selectedIds.clear();

    this.itemsService.getByLoadingListId(loadingListId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          this.items = res || [];
          this.availableStatuses = Array.from(
            new Set(this.items.map(i => (i.loadingListItemStatus || '').toUpperCase()).filter(Boolean))
          ).sort();

          this.searchTerm = '';
          this.filterStatus = '';
          this.applyFilters();
        },
        error: (err) => {
          console.error('Errore getByLoadingListId', err);
          this.items = [];
          this.applyFilters();
        }
      });
  }

  private resetLocalState(): void {
    this.isLoading = false;
    this.items = [];
    this.filteredItems = [];
    this.pagedItems = [];
    this.availableStatuses = [];
    this.searchTerm = '';
    this.filterStatus = '';
    this.selectedIds.clear();
    this.itemsPerPage = 10;
    this.currentPage = 1;
    this.isExporting = false;
    this.isPrinting = false;
  }

  applyFilters(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    const status = (this.filterStatus || '').trim().toUpperCase();

    this.filteredItems = this.items.filter(item => {
      const haystack = [
        item.loadingListItemId,
        item.loadingListItemParcelId,
        item.loadingListItemStatus,
        item.loadingListItemCreatedBy,
        item.loadingListItemCreatedAt
      ].join(' ').toLowerCase();

      const matchTerm = !term || haystack.includes(term);
      const matchStatus = !status || (item.loadingListItemStatus || '').toUpperCase() === status;

      return matchTerm && matchStatus;
    });

    this.currentPage = 1;
    this.refreshPagedItems();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filterStatus = '';
    this.applyFilters();
  }

  refreshPagedItems(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.pagedItems = this.filteredItems.slice(start, end);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage));
  }

  onPageChange(page: number): void {
    const totalPages = this.getTotalPages();
    const safe = Math.min(Math.max(page, 1), totalPages);
    this.currentPage = safe;
    this.refreshPagedItems();
  }

  setItemsPerPage(n: number): void {
    this.itemsPerPage = n;
    this.currentPage = 1;
    this.refreshPagedItems();
  }

  trackByItemId(_: number, item: LoadingListItems): number {
    return item.loadingListItemId;
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  toggleSelection(id: number): void {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
  }

  areAllSelected(): boolean {
    return this.filteredItems.length > 0 && this.filteredItems.every(i => this.selectedIds.has(i.loadingListItemId));
  }

  selectAll(): void {
    this.filteredItems.forEach(i => this.selectedIds.add(i.loadingListItemId));
  }

  deselectAll(): void {
    this.selectedIds.clear();
  }

  getTotalCount(): number {
    return this.items.length;
  }

  getShotCount(): number {
    return this.items.filter(i => (i.loadingListItemStatus || '').toUpperCase() === 'PICKERED').length;
  }

  getShotCountFiltered(): number {
    return this.filteredItems.filter(i => (i.loadingListItemStatus || '').toUpperCase() === 'PICKERED').length;
  }

  getItemStatusStyle(status?: string): { [key: string]: string } {
    const s = (status || '').toUpperCase();
    if (s === 'PICKERED') return { backgroundColor: '#d1fae5', color: '#065f46' };
    if (s === 'CHECKED') return { backgroundColor: '#d1fae5', color: '#065f46' };
    if (s === 'TO_CHECK') return { backgroundColor: '#fff3cd', color: '#92400e' };
    return { backgroundColor: '#e9ecef', color: '#495057' };
  }

  printSelected(): void {
    // qui puoi poi chiamare endpoint vero; per ora placeholder
    if (this.selectedIds.size === 0) return;
    this.isPrinting = true;
    setTimeout(() => (this.isPrinting = false), 600);
  }

  exportSelected(): void {
    // qui puoi poi chiamare endpoint vero; per ora placeholder
    if (this.selectedIds.size === 0) return;
    this.isExporting = true;
    setTimeout(() => (this.isExporting = false), 600);
  }
}
